/** @jsxImportSource @emotion/react */
import { ReactNode, MouseEvent, createContext, useState, useCallback, useMemo, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { css, SerializedStyles } from "@emotion/react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";

import { Button } from "../components";
import { Props as ButtonProps } from "../components/Button";

const actionContainer = css`
  justify-content: space-around;
`;

const actionButton = css`
  width: 172px;
`;

interface Action extends Omit<ButtonProps, "onClick"> {
  label: string;
  primary?: boolean;
  onClick?: (e: MouseEvent, hideModal: () => void) => void;
}

export interface CustomizeStyle {
  actionContainer?: SerializedStyles;
  contentContainer?: SerializedStyles;
}

interface Args {
  title?: string;
  content?: string | JSX.Element;
  actions?: Action[];
  maxWidth?: false | "xs" | "sm" | "md" | "lg" | "xl" | undefined;
  customizeStyle?: CustomizeStyle;
  disableBackDropClick?: boolean;
}

type ContextProps = [(args: Args) => void, () => void];

interface ProviderProps {
  children: ReactNode;
}

const ModalContext = createContext<Partial<ContextProps>>([]);

const ModalProvider = ({ children }: ProviderProps): JSX.Element => {
  const { listen } = useHistory();
  const [open, setOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState();
  const [modalContent, setModalContent] = useState();
  const [modalActions, setModalActions] = useState<Action[]>([]);
  const [modalWidth, setModalWidth] = useState<false | "xs" | "sm" | "md" | "lg" | "xl" | undefined>("xs");
  const [modalContentContainer, setModalContentContainer] = useState<SerializedStyles>();
  const [modalActionContainer, setModalActionContainer] = useState();
  const [modalDisableBackDropClick, setModalDisableBackDropClick] = useState(false);

  const showModal = useCallback(
    ({ title, content, actions, maxWidth = "xs", customizeStyle, disableBackDropClick } = {}) => {
      setOpen(true);
      if (title) setModalTitle(title);
      if (content) setModalContent(content);
      if (actions) setModalActions(actions);
      if (maxWidth) setModalWidth(maxWidth);
      if (customizeStyle?.contentContainer) setModalContentContainer(customizeStyle?.contentContainer);
      if (customizeStyle?.actionContainer) setModalActionContainer(customizeStyle?.actionContainer);
      if (disableBackDropClick) setModalDisableBackDropClick(true);
    },
    []
  );

  const hideModal = useCallback(() => {
    setOpen(false);
  }, []);

  const reset = useCallback(() => {
    setModalTitle(undefined);
    setModalContent(undefined);
    setModalActions([]);
    setModalWidth("xs");
    setModalActionContainer(undefined);
    setModalDisableBackDropClick(false);
  }, []);

  const value: ContextProps = useMemo(() => [showModal, hideModal], [showModal, hideModal]);

  useEffect(() => {
    const unlisten = listen(() => {
      if (open) hideModal();
    });

    return (): void => unlisten();
  }, [listen, open, hideModal]);

  return (
    <ModalContext.Provider value={value}>
      <Dialog
        fullWidth
        open={open}
        onClose={hideModal}
        onExited={reset}
        maxWidth={modalWidth}
        disableEnforceFocus
        disableBackdropClick={modalDisableBackDropClick}
        disableEscapeKeyDown={modalDisableBackDropClick}
      >
        {modalTitle && <DialogTitle>{modalTitle}</DialogTitle>}
        {modalContent && typeof modalContent === "string" ? (
          <DialogContent css={modalContentContainer}>{modalContent}</DialogContent>
        ) : (
          <>{modalContent}</>
        )}
        {modalActions.length > 0 && (
          <DialogActions css={modalActionContainer || actionContainer}>
            {modalActions.map(({ primary, color, label, variant = "contained", onClick, ...rest }, i) => (
              <Button
                // eslint-disable-next-line react/no-array-index-key
                key={`btn-${i}`}
                variant={variant}
                color={primary ? "primary" : color}
                onClick={(e): void => onClick && onClick(e, hideModal)}
                css={actionButton}
                {...rest}
              >
                {label}
              </Button>
            ))}
          </DialogActions>
        )}
      </Dialog>
      {children}
    </ModalContext.Provider>
  );
};

const useModal = (): ContextProps => useContext(ModalContext) as ContextProps;

export { ModalProvider, useModal };
